export type Workers =
  | "autopilot"
  | "employee-engine"
  | "cal"
  | "images"
  | "brandfetch"
  | "huddle"
  | "self-driving"
  | "flags"
  | "atlas-v2"
  | "unsplash"
  | "iconfinder"
  | "teams-engine-v2"
  | "email-engine-v2"
  | "slack-engine-v2"
  | "twilio"
  | "unlayer-engine"
  | "poll-position"
  | "media"
  | "slack-admin"
  | "tremendous"
  | "showroom"
  | "segment-engine"
  | "key";

const getActualServiceUrl = (service: Workers): string => {
  switch (service) {
    case "poll-position":
      return "survey";
    case "showroom":
      return "communication";
    case "unlayer-engine":
      return "unlayer";
    case "segment-engine":
      return "segment";
    case "autopilot":
    case "employee-engine":
    case "cal":
    case "images":
    case "brandfetch":
    case "huddle":
    case "self-driving":
    case "flags":
    case "atlas-v2":
    case "unsplash":
    case "iconfinder":
    case "teams-engine-v2":
    case "email-engine-v2":
    case "slack-engine-v2":
    case "twilio":
    case "media":
    case "slack-admin":
    case "tremendous":
    case "key":
    default:
      return service;
  }
};

/**
 * Vite replaces `import.meta.env` while it builds the browser app, and wrangler builds the worker
 * with esbuild, which does not. So every `serviceURL` call in the worker read `undefined` and
 * returned the local branch: a poll option saved by the headless render pointed at localhost.
 *
 * `api/app.ts` installs the worker's own binding here on each request, beside the settings.
 */
let installedEnvironment: string | undefined;

export function setServiceEnvironment(environment: string | undefined): void {
  installedEnvironment = environment;
}

export function serviceURL(service: Workers): string {
  return serviceURLForEnvironment(
    service,
    import.meta.env?.VITE_SERVICE_ENVIRONMENT ?? installedEnvironment,
  );
}

export function serviceURLForEnvironment(
  service: Workers,
  environment: string | undefined,
): string {
  if (environment === undefined || environment === "local") {
    return {
      autopilot: "http://localhost:9127",
      "employee-engine": "http://localhost:9001/",
      cal: "http://localhost:9008/",
      images: "http://localhost:9101/",
      brandfetch: "http://localhost:9108/",
      huddle: "http://localhost:9107/",
      "self-driving": "http://localhost:9109/",
      flags: "http://localhost:9110/",
      "atlas-v2": "http://localhost:9115/",
      unsplash: "http://localhost:9119/",
      iconfinder: "http://localhost:9126/",
      "teams-engine-v2": "http://localhost:9211/",
      "email-engine-v2": "http://localhost:9111/",
      "slack-engine-v2": "http://localhost:9212/",
      twilio: "http://localhost:9103/",
      "unlayer-engine": "http://localhost:9135/",
      "poll-position": "http://localhost:9128/",
      media: "http://localhost:9116/",
      "slack-admin": "http://localhost:9213/",
      tremendous: "http://localhost:9132/",
      showroom: "http://localhost:9133/",
      "segment-engine": "http://localhost:9136/",
      key: "http://localhost:8787",
    }[service];
  }

  return `https://${getActualServiceUrl(service)}${environment}.changeengine.com/`;
}
